import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Info } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { DOMAIN } from "@/lib/constants";
import PageHero from "@/components/site/PageHero";
import { inputCls, labelCls, submitLead, SuccessPanel } from "@/components/site/leadForm";

const ESTABELECIMENTOS = [
  "Posto de combustível / eletroposto",
  "Empresa / frota",
  "Supermercado / centro comercial",
  "Hotel / pousada",
  "Estacionamento",
  "Condomínio",
  "Rodovia / ponto estratégico",
  "Residência",
  "Outro",
];
const TENSOES = ["220 V", "380 V", "440 V", "Não sei informar"];
const TRANSFORMADORES = ["Transformador próprio", "Transformador compartilhado", "Não possuo transformador", "Não sei informar"];
const POTENCIAS_DESEJADAS = ["Não sei — preciso de orientação", "Até 60 kW", "60 a 180 kW", "Acima de 180 kW"];

export default function SimulePage() {
  useSeo(
    "Simule seu Projeto de Recarga VE | Análise de Infraestrutura — HB Charger",
    "Informe tipo de estabelecimento, tensão disponível, transformador e demanda: nossa engenharia analisa a viabilidade da sua estação de recarga para veículos elétricos."
  );

  const [form, setForm] = useState({
    nome: "", telefone: "", cidade: "",
    tipo_empreendimento: ESTABELECIMENTOS[0], tensao_disponivel: TENSOES[0],
    transformador: TRANSFORMADORES[0], demanda_aproximada: "",
    potencia_desejada: POTENCIAS_DESEJADAS[0], mensagem: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [whatsMsg, setWhatsMsg] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.telefone.trim() || !form.cidade.trim()) {
      toast.error("Preencha nome, WhatsApp e cidade/UF.");
      return;
    }
    const payload = {
      tipo: "simulacao",
      nome: form.nome.trim(),
      telefone: form.telefone.trim(),
      cidade: form.cidade.trim(),
      tipo_empreendimento: form.tipo_empreendimento,
      tensao_disponivel: form.tensao_disponivel,
      transformador: form.transformador,
      demanda_aproximada: form.demanda_aproximada.trim() ? `${form.demanda_aproximada.trim()} kW` : null,
      potencia_desejada: form.potencia_desejada,
      mensagem: form.mensagem.trim() || null,
    };
    const msg =
      `Olá HB Solar! Fiz a simulação de projeto no site.\n\n*Nome:* ${payload.nome}\n*WhatsApp:* ${payload.telefone}\n*Cidade/UF:* ${payload.cidade}\n*Estabelecimento:* ${payload.tipo_empreendimento}\n*Tensão disponível:* ${payload.tensao_disponivel}\n*Transformador:* ${payload.transformador}\n*Demanda aproximada:* ${payload.demanda_aproximada || "—"}\n*Potência desejada:* ${payload.potencia_desejada}\n\nVim pelo site ${DOMAIN}`;
    setWhatsMsg(msg);
    setSending(true);
    const ok = await submitLead(payload, msg);
    setSending(false);
    if (ok) {
      toast.success("Simulação registrada! Conclua o envio no WhatsApp que abriu.");
    } else {
      toast.error("Não conseguimos registrar agora — conclua pelo WhatsApp que abriu.");
    }
    setSent(true);
  };

  const reset = () => {
    setForm({ nome: "", telefone: "", cidade: "", tipo_empreendimento: ESTABELECIMENTOS[0], tensao_disponivel: TENSOES[0], transformador: TRANSFORMADORES[0], demanda_aproximada: "", potencia_desejada: POTENCIAS_DESEJADAS[0], mensagem: "" });
    setSent(false);
    setWhatsMsg("");
  };

  return (
    <>
      <PageHero
        testId="simulate-page-title"
        eyebrow="Simule seu projeto"
        title="Conte-nos sobre sua infraestrutura"
        desc="Estes dados permitem que nossa engenharia faça uma pré-análise do seu caso. A simulação não substitui o levantamento técnico e não constitui dimensionamento definitivo."
      />

      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {sent ? (
            <SuccessPanel
              title="Simulação enviada com sucesso!"
              text="Nossa engenharia recebeu os dados da sua infraestrutura e vai retornar com a análise. Para agilizar, conclua o envio no WhatsApp que abriu."
              whatsMsg={whatsMsg}
              onReset={reset}
            />
          ) : (
            <form data-testid="simulate-form" onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="s-nome" className={labelCls}>Nome *</label>
                  <input id="s-nome" data-testid="simulate-input-name" className={inputCls} value={form.nome} onChange={set("nome")} placeholder="Seu nome" required />
                </div>
                <div>
                  <label htmlFor="s-tel" className={labelCls}>WhatsApp *</label>
                  <input id="s-tel" data-testid="simulate-input-phone" type="tel" className={inputCls} value={form.telefone} onChange={set("telefone")} placeholder="(73) 99999-9999" required />
                </div>
              </div>
              <div>
                <label htmlFor="s-cidade" className={labelCls}>Cidade e Estado *</label>
                <input id="s-cidade" data-testid="simulate-input-city" className={inputCls} value={form.cidade} onChange={set("cidade")} placeholder="Ex: Itabuna — BA" required />
              </div>
              <div>
                <label htmlFor="s-estab" className={labelCls}>Tipo de estabelecimento</label>
                <select id="s-estab" data-testid="simulate-select-business" className={inputCls} value={form.tipo_empreendimento} onChange={set("tipo_empreendimento")}>
                  {ESTABELECIMENTOS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="s-tensao" className={labelCls}>Tensão disponível</label>
                  <select id="s-tensao" data-testid="simulate-select-voltage" className={inputCls} value={form.tensao_disponivel} onChange={set("tensao_disponivel")}>
                    {TENSOES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="s-trafo" className={labelCls}>Transformador</label>
                  <select id="s-trafo" data-testid="simulate-select-transformer" className={inputCls} value={form.transformador} onChange={set("transformador")}>
                    {TRANSFORMADORES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="s-demanda" className={labelCls}>Demanda aproximada (kW)</label>
                  <input id="s-demanda" data-testid="simulate-input-demand" type="number" min="0" className={inputCls} value={form.demanda_aproximada} onChange={set("demanda_aproximada")} placeholder="Ex: 75" />
                </div>
                <div>
                  <label htmlFor="s-pot" className={labelCls}>Potência desejada do carregador</label>
                  <select id="s-pot" data-testid="simulate-select-power" className={inputCls} value={form.potencia_desejada} onChange={set("potencia_desejada")}>
                    {POTENCIAS_DESEJADAS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="s-msg" className={labelCls}>Observações</label>
                <textarea id="s-msg" data-testid="simulate-input-message" rows={3} className={inputCls} value={form.mensagem} onChange={set("mensagem")} placeholder="Algo mais que devemos saber? (opcional)" />
              </div>

              <div className="flex items-start gap-3 rounded-2xl bg-blue-50 border border-blue-200 p-5">
                <Info className="h-5 w-5 shrink-0 text-blue-600 mt-0.5" />
                <p className="text-xs leading-relaxed text-slate-600">
                  A potência efetiva de carregamento depende da infraestrutura elétrica do
                  local, da demanda existente e das condições da concessionária. Nenhuma
                  potência é garantida sem análise técnica presencial.
                </p>
              </div>

              <button
                data-testid="simulate-submit-button"
                type="submit"
                disabled={sending}
                className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#0B192C] hover:bg-[#12243d] text-white font-extrabold text-base px-7 py-4 transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? "Enviando..." : "Enviar para análise da engenharia"}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
